import { ReadonlyVec2 } from 'gl-matrix';
import { View } from '../view/View';
import { PlaneView } from '../view/views/PlaneView';
import { EventHandler } from './EventHandler';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from './EventHandlerResult';
import { ModifierKeys } from './ModifierKeys';
import { MOUSE_BUTTONS_NONE } from './MouseButton';

/**
 * Describes how much crosshair interaction is available in a given state.
 *
 * - `'full'`           – all interaction enabled, normal visuals.
 * - `'roots-only'`     – only the root handles at the line endpoints are
 *                        interactive; the line bodies and centre handle are
 *                        disabled. Lines are rendered with a visual indicator
 *                        to communicate this.
 * - `'drawing-stroke'` – mouse button is held during a drawing-tool stroke;
 *                        nothing is interactive. The disabled visual from
 *                        `'roots-only'` is preserved (the tool is still
 *                        active), but no additional visual changes are made
 *                        because the disable is momentary.
 * - `'none'`           – nothing is interactive and no special visual change
 *                        is shown (pan, windowing, etc.).
 */
export enum CrosshairInteractionMode {
    Full = 'full',
    RootsOnly = 'roots-only',
    DrawingStroke = 'drawing-stroke',
    None = 'none',
}

/**
 * Superclass for all the {@link Tool Tools}.\
 * This defines all the methods and general structure of tools.\
 */
export abstract class Tool implements EventHandler {
    abstract toolType: string;
    /**
     * Method to render render the tool in the View
     */
    public render(): void {
        // Empty implementation
    }

    // Start the tool action
    public onActivate(): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MousePress event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _button The button that triggered the event
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _coordinates The mouse position in client pixel coordinates at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onMousePress(
        _button: number,
        _modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        _view: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseRelease event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _coordinates The mouse position in client pixel coordinates at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2,
        _view: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseMove event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _coordinates The mouse position in client pixel coordinates at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onMouseMove(
        _coordinates: ReadonlyVec2,
        _buttons: number,
        _view: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Called by {@link ToolManager} after an external view change
     * (e.g. zoom or scroll) to let the tool update its visuals
     * for the new view state without a real mouse event.
     *
     * Default implementation delegates to
     * {@link onMouseMove} with `buttons: 0` for backward
     * compatibility. Tools may override for cleaner semantics.
     */
    public onViewChanged(coordinates: ReadonlyVec2, view: View): void {
        this.onMouseMove(coordinates, MOUSE_BUTTONS_NONE, view);
    }

    /**
     * MouseWheel event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _delta The direction of the MouseWheel event.
     * -1 if the wheel was scrolled forwards, 1 if the wheel was scrolled backwards.
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _coordinates The mouse position in client pixel coordinates at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onMouseWheel(
        _delta: -1 | 1,
        _modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * MouseDoubleClick event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _coordinates The mouse position in client pixel coordinates at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onMouseDoubleClick(
        _coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * KeyPress event that was caught by the canvas and propagated by {@link ToolManager} to this {@link Tool}.
     * @param _key The key that triggered the event
     * @param _modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param _view The {@link View} on which the event occured
     * @returns An {@link EventHandlerResult} that informs the {@link ToolManager}
     * how this {@link Tool} reacted to that event and how to proceed.
     */
    public onKeyPress(
        _key: string,
        _modifierKeys: ModifierKeys,
        _view: View
    ): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Whether this tool currently has an in-progress action
     * (e.g. a brush stroke or registration drag). Used by
     * {@link ToolManager} to prevent the global pan (or other
     * actions) from starting while another action is active.
     */
    public isActionInProgress(): boolean {
        return false;
    }

    /** Whether the user is actively manipulating with the mouse
     *  button held (e.g., mid-brush-stroke, free-form line
     *  segment, registration drag). Default: same as
     *  isActionInProgress(). Override in tools where the action
     *  session is broader than a single stroke (e.g., LineDrawTool
     *  multi-click). */
    public isStrokeInProgress(): boolean {
        return this.isActionInProgress();
    }

    /**
     * The view in which this tool is currently performing a drawing
     * action, or undefined when no drawing action is in progress.
     * Override in drawing tools to enable view-relative blocking of
     * crosshair and pan interactions in non-drawing viewports.
     */
    public getDrawingView(): PlaneView | undefined {
        return undefined;
    }

    /** Whether this tool uses the given mouse button for its own
     *  purposes during an active action (e.g. LineDrawTool uses
     *  right-click to undo the last point). When true for a
     *  button, global coordinators suppress early visual feedback
     *  for that button. */
    public usesButtonDuringAction(_button: number): boolean {
        return false;
    }

    /** Re-apply this tool's custom cursor. Called by ToolManager
     *  after the global pan cursor is cleared so the tool cursor
     *  is restored without waiting for the next mouse move. */
    public refreshCursor(): void {
        // No-op by default — override in tools with custom cursors.
    }

    /**
     * Returns the crosshair interaction mode for the viewport asking.
     * Override in tools that need a non-default policy.
     *
     * @param isDrawingActiveInOtherView True when this tool has an active
     *   drawing session in a *different* viewport.  Precomputed by
     *   {@link ToolManager} because tools cannot call back into it.
     */
    public getCrosshairInteractionMode(
        isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        if (isDrawingActiveInOtherView) {
            return CrosshairInteractionMode.None;
        }
        if (this.isStrokeInProgress()) {
            return CrosshairInteractionMode.None;
        }
        return CrosshairInteractionMode.Full;
    }

    /**
     * Shared crosshair policy for contouring drawing tools (brush,
     * line-draw, etc.).  Between strokes the crosshair is reduced to
     * roots-only so the tool stays visible; during a stroke the mouse
     * button is held so interaction is silently blocked; other viewports
     * are fully blocked while a drawing session is active.
     */
    protected static contouringCrosshairMode(
        isDrawingActiveInOtherView: boolean,
        isStrokeInProgress: boolean
    ): CrosshairInteractionMode {
        if (isDrawingActiveInOtherView) {
            return CrosshairInteractionMode.None;
        }
        if (isStrokeInProgress) {
            return CrosshairInteractionMode.DrawingStroke;
        }
        return CrosshairInteractionMode.RootsOnly;
    }

    /**
     * Shared crosshair policy for gizmo-drag tools (transform,
     * registration, etc.).  Between drags the crosshair is reduced to
     * roots-only; during a drag it is silently blocked.  Other viewports
     * are not affected because gizmo tools do not have a drawing view.
     */
    protected static gizmoCrosshairMode(
        isStrokeInProgress: boolean
    ): CrosshairInteractionMode {
        return isStrokeInProgress
            ? CrosshairInteractionMode.DrawingStroke
            : CrosshairInteractionMode.RootsOnly;
    }

    // End the tool action
    public onDeactivate(): EventHandlerResult {
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Final cleanup called by {@link ToolManager} when this tool
     * instance is being permanently replaced or removed.  Override
     * in tools that own long-lived resources (e.g. gizmos) that
     * should survive temporary deactivation (mouseenter/leave)
     * but must be released when the tool is discarded.
     */
    public dispose(): void {
        // No-op by default.
    }
}
